import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button } from '@mui/material';

import { IExpense } from '../../types/expense';

import { AppConfig } from '../../config/appConfig';
import { useUserContext } from '../../contexts/userContext';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import "../../styles/entity/expenseList.scss";

export interface IExpenseTableProps {
	expenses?: IExpense[];
	name?: string;
	category?: string;
	spreadAcrossMaterials?: boolean;
	isActive?: boolean;
}

const ExpenseTable: React.FunctionComponent<IExpenseTableProps> = ({ expenses, name, category, spreadAcrossMaterials, isActive }) => {
	const userContext = useUserContext();
	const [dataView, setDataView] = useState<IExpense[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.expenseListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);

	useEffect(() => {
		if (expenses) {
			let newData = expenses.filter((item) => {
				if (name && name != "" && (item.name?.toLowerCase().indexOf(name?.toLowerCase()) < 0)) {
					return false;
				}
				if (category && category != "" && (item.category?.toLowerCase().indexOf(category?.toLowerCase()) < 0)) {
					return false;
				}
				if (spreadAcrossMaterials != undefined && item.spreadAcrossMaterials != spreadAcrossMaterials) {
					return false;
				}
				if (isActive != undefined && item.isActive != isActive) {
					return false;
				}
				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "name": {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
					case "category": {
						newData.sort((a, b) => UtilityService.compareStrings(a.category, b.category));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			setPage(0);
			setIsLoadingExpenses(false);
		}
	}, [expenses, name, category, spreadAcrossMaterials, isActive, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-name">
					<p onClick={() => handleHeaderClick("name")}>
						Name
						{" "}
						{(sortField == "name" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "name" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-category">
					<p onClick={() => handleHeaderClick("category")}>
						Category
						{" "}
						{(sortField == "category" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "category" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-spread-across-materials">
					<p>
						Distributed
					</p>
				</div>
				<div className="col-active">
					<p>
						Active
					</p>
				</div>
				{userContext.permissions?.canManageExpenses && <div className="col-controls">{""}</div>}
			</div>
			<Spinner dependency={isLoadingExpenses} text="Loading...">
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
								<div className="row" key={item.id}>
									<div className="col-name">{item.name}</div>
									<div className="col-category">{item.category}</div>
									<div className="col-spread-across-materials">{item.spreadAcrossMaterials ? "Yes" : "No"}</div>
									<div className="col-active">{item.isActive ? "Active" : "Inactive"}</div>
									{userContext.permissions?.canManageExpenses &&
										<div className="col-controls">
											<p>
												<NavLink to={"/expense/" + item.id}>
													<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
													{" "}
													Edit
												</NavLink>
											</p>
										</div>
									}
								</div>
							))
						) : (<div className="no-results">0 results</div>)}
				</div>
				{dataView?.length > 0 &&
					<div className="pager">
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
					</div>
				}
			</Spinner>
		</div>
	)
}

export default ExpenseTable;